export const UK = { blue: '#012169', red: '#C8102E', gold: '#E6A817', white: '#FFFFFF' };

export const LEASE_FILTERS = ['All', 'Active', 'Notice', 'Expiring', 'Expired', 'Ended', 'Ready to Renew'] as const;

export type LeaseFormState = {
  property_address: string;
  tenant_name: string;
  start_date: string;
  end_date: string;
  monthly_rent: string;
  deposit: string;
  tenancy_type: string;
  notice_period: string;
  room_number: string;
  payment_day: string;
  deposit_scheme: string;
  deposit_registered_date: string;
  deposit_ref: string;
  agreement_signed: boolean;
  notes: string;
  renewal_date: string;
  break_clause_months: string;
  late_fee_amount: string;
  late_fee_grace_days: string;
};

export const DEFAULT_LEASE_FORM: LeaseFormState = {
  property_address: '', tenant_name: '', start_date: '', end_date: '',
  monthly_rent: '', deposit: '', tenancy_type: 'AST', notice_period: '2', room_number: '',
  payment_day: '1', deposit_scheme: '', deposit_registered_date: '', deposit_ref: '',
  agreement_signed: false, notes: '', renewal_date: '',
  break_clause_months: '', late_fee_amount: '', late_fee_grace_days: '3',
};

const toDateStr = (v: unknown) => (v ? String(v).split('T')[0] : '');

export function leaseToForm(l: Record<string, any>): LeaseFormState {
  return {
    property_address: l.property_address || '',
    tenant_name: l.tenant_name || '',
    start_date: toDateStr(l.start_date),
    end_date: toDateStr(l.end_date),
    monthly_rent: String(l.monthly_rent ?? ''),
    deposit: String(l.deposit ?? ''),
    tenancy_type: l.tenancy_type || 'AST',
    notice_period: String(l.notice_period ?? '2'),
    room_number: l.room_number || '',
    payment_day: String(l.payment_day ?? '1'),
    deposit_scheme: l.deposit_scheme || '',
    deposit_registered_date: toDateStr(l.deposit_registered_date),
    deposit_ref: l.deposit_ref || '',
    agreement_signed: !!l.agreement_signed,
    notes: l.notes || '',
    renewal_date: toDateStr(l.renewal_date),
    break_clause_months: l.break_clause_months != null ? String(l.break_clause_months) : '',
    late_fee_amount: l.late_fee_amount != null ? String(l.late_fee_amount) : '',
    late_fee_grace_days: l.late_fee_grace_days != null ? String(l.late_fee_grace_days) : '3',
  };
}

export function renewFromLease(l: Record<string, any>): LeaseFormState {
  const oldEnd = new Date(l.end_date);
  const newEnd = new Date(oldEnd);
  newEnd.setFullYear(newEnd.getFullYear() + 1);
  newEnd.setDate(newEnd.getDate() - 1);
  const toISO = (d: Date) => d.toISOString().split('T')[0];
  return {
    ...leaseToForm(l),
    start_date: toISO(oldEnd),
    end_date: toISO(newEnd),
    deposit_registered_date: '',
    deposit_ref: '',
    agreement_signed: false,
    notes: '',
    renewal_date: '',
  };
}

export const fmt = (d: string) => d ? new Date(d).toLocaleDateString('en-GB') : '—';

export const ordinal = (n: number) => {
  const s = ['th', 'st', 'nd', 'rd'];
  const v = n % 100;
  return n + (s[(v - 20) % 10] || s[v] || s[0]);
};

export function isLeaseEnded(l: any) {
  const status = String(l.lease_status || '').toLowerCase();
  return status === 'ended' || !!l.ended_at;
}

export function isLeaseActive(l: any) {
  const status = String(l.lease_status || '').toLowerCase();
  return !isLeaseEnded(l) && status !== 'notice_given' && Number(l.days_to_expiry) >= 0;
}

export function readyToRenew(l: any) {
  return isLeaseEnded(l) && !l.has_renewal;
}

export function leaseAccent(l: any): string {
  const status = String(l.lease_status || l.computed_status || '').toLowerCase();
  if (status === 'ended' || l.ended_at) return '#94a3b8';
  if (status === 'notice_given') return UK.blue;
  const days = Number(l.days_to_expiry);
  if (days < 0) return UK.red;
  if (days <= 60) return UK.gold;
  return '#16a34a';
}

export function expiryBadge(l: any) {
  const status = String(l.lease_status || '').toLowerCase();
  if (status === 'ended' || l.ended_at) return { label: 'Ended', bg: '#f1f5f9', color: '#6b7280' };
  if (status === 'notice_given') {
    const nd = Number(l.days_to_expiry);
    return { label: nd >= 0 ? `Notice — ${nd}d` : 'Notice passed', bg: '#dbeafe', color: UK.blue };
  }
  const days = Number(l.days_to_expiry);
  if (days < 0) return { label: 'Expired', bg: '#fee2e2', color: UK.red };
  if (days === 0) return { label: 'Ends today', bg: '#fef3c7', color: '#d97706' };
  if (days <= 60) return { label: `Expiring — ${days}d`, bg: '#fef3c7', color: '#b45309' };
  return { label: 'Active', bg: '#dcfce7', color: '#16a34a' };
}

export function depositBadge(l: any) {
  if (!l.deposit || Number(l.deposit) === 0) return null;
  if (l.deposit_scheme && l.deposit_scheme !== 'None') {
    return { label: l.deposit_scheme, bg: '#dcfce7', color: '#16a34a' };
  }
  const deadline = new Date(new Date(l.start_date).getTime() + 30 * 86400000);
  if (new Date() > deadline) return { label: 'Unprotected', bg: '#fee2e2', color: UK.red };
  const daysLeft = Math.floor((deadline.getTime() - Date.now()) / 86400000);
  return { label: `Register in ${daysLeft}d`, bg: '#fef3c7', color: '#b45309' };
}

export function s13StatusLabel(inc: any) {
  if (inc.status === 'effective') return { label: 'Effective', bg: '#dcfce7', color: '#16a34a' };
  if (inc.notice_served) return { label: 'Notice served', bg: '#dbeafe', color: UK.blue };
  return { label: 'Draft', bg: '#fef3c7', color: '#92400e' };
}
